import moment from 'moment';
import { loadJavascript } from '../../common/asset-loader';
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import { trackEvent } from '../../analytics/gtm';
import './Cookiebot.less';
import { CHANNEL_PORTAL, CHANNEL_MOBILE } from '../turboLive/constants';
import { useEffect, useReducer } from 'react';

type ChannelType = typeof CHANNEL_PORTAL | typeof CHANNEL_MOBILE;
let CHANNEL: ChannelType = CHANNEL_MOBILE;

const addBackdrop = () => {
    if (document.getElementById('CookieBannerOverlay')) {
        return;
    }

    const overlayDiv = document.createElement('div');
    overlayDiv.setAttribute('id', 'CookieBannerOverlay');
    overlayDiv.addEventListener('click', e => {
        trackEvent('Cookiebot', 'CookieBannerOverlay');
        e.stopPropagation();
        e.preventDefault();
    });
    document.getElementsByTagName('body')[0].appendChild(overlayDiv);
};

const removeBackdrop = () => document.getElementById('CookieBannerOverlay')?.remove();

const addClassToAnimate = () =>
    document.getElementById('CybotCookiebotDialog')?.classList.add('CybotCookiebotDialogTransitionClose');

const onDialogDisplay = () => {
    if (!window.Cookiebot || window.Cookiebot?.isOpen) {
        return;
    }

    setTimeout(() => {
        trackEvent('Cookiebot', 'CookiebotOnDialogDisplay');
    }, 5000);

    window.Cookiebot.isOpen = true;

    if (CHANNEL === CHANNEL_MOBILE) {
        addBackdrop();

        // ReactDOM.render(<CookiebotCloseButton />, document.getElementById('CybotCookiebotDialogHeader'));
        disableBodyScroll(document.getElementById('CybotCookiebotDialogBodyContent'));
        disableBodyScroll(document.getElementById('CybotCookiebotDialogTabContentDetails'));
        disableBodyScroll(document.getElementById('CybotCookiebotDialogDetailBodyContentTextAbout'));
    }

    const customizeCloseButton = () => {
        Array.from(document.getElementsByClassName('CybotCookiebotBannerCloseButton')).forEach(original => {
            const clone = original.cloneNode(true);
            clone.addEventListener('click', () => {
                trackEvent('Cookiebot', 'CybotCookiebotBannerCloseButton');
                closeCookiebotDialog();
            });
            original.parentNode.replaceChild(clone, original);
        });
    };

    const customizeDomain = () => {
        const elmId = document.getElementById('CybotCookiebotDialogDetailBulkConsentList');
        elmId.innerHTML = `
            <dt><a target="_blank" href="https://www.nike.sk"">www.nike.sk</a></dt>
            <dt><a target="_blank" href="https://m.nike.sk">m.nike.sk</a></dt>
            <dt><a target="_blank" href="https://lp.nike.sk">lp.nike.sk</a></dt>
`;
    };

    const customizeDetailFooter = () => {
        const footer = document.getElementById('CybotCookiebotDialogDetailFooter');

        if (!window.Cookiebot || !footer) {
            return;
        }

        const consentID = window.Cookiebot?.consentID;
        const consentDate = moment(window.Cookiebot?.consentUTC).format('DD.MM.YYYY');

        if (!window.Cookiebot?.declined && !window.Cookiebot?.consented) {
            footer.innerHTML = '';
            return;
        }

        footer.innerHTML = `
        <div id="CookieConsentInfo">
            <p id="CookieConsentInfoID">
                <span>ID súhlasu: </span>
                <span id="CookieConsentInfoIDValue" class="text-bold">
                    ${consentID}
                </span>
            </p>
            <p id="CookieConsentInfoIDDate">
                <span>Dátum súhlasu: </span>
                <span class="text-bold">${consentDate}</span>
            </p>
        </div>
`;
    };

    customizeDomain();
    customizeCloseButton();
    customizeDetailFooter();
};

const onHide = () => {
    if (!window.Cookiebot) {
        return;
    }
    window.Cookiebot.isOpen = false;

    if (CHANNEL === CHANNEL_MOBILE) {
        removeBackdrop();
        addClassToAnimate();

        enableBodyScroll(document.getElementById('CybotCookiebotDialogBodyContent'));
        enableBodyScroll(document.getElementById('CybotCookiebotDialogTabContentDetails'));
        enableBodyScroll(document.getElementById('CybotCookiebotDialogDetailBodyContentTextAbout'));
    }
};

const onAccept = () => {
    if (window.Cookiebot?.isOpen) {
        trackEvent('Cookiebot', 'CookiebotOnAccept');
        onHide();
    } else {
        trackEvent('Cookiebot', 'Auto');
    }
};

const onDecline = () => {
    if (window.Cookiebot?.isOpen) {
        trackEvent('Cookiebot', 'CookiebotOnDecline');
        onHide();
    } else {
        trackEvent('Cookiebot', 'Auto');
    }
};

export const initializeCookiebot = (options: { channel: ChannelType }) => {
    if ((window?.location?.search || '').includes('embedded')) {
        return Promise.resolve();
    }
    if (options?.channel) {
        CHANNEL = options?.channel;
    }
    return loadJavascript('Cookiebot', 'https://consent.cookiebot.com/uc.js', {
        'data-cbid': '50aa78a4-a477-4cc6-afb9-f602a14ffcd9',
        'data-blockingmode': 'manual'
    })
        .then(() => {
            if (window.Cookiebot?.hasResponse) {
                checkConsentBefore3Months();
            }
        })
        .catch(error => {
            console.error('Load consent cookie error', error);
        });
};

export const showCookiebotDialog = () => {
    window.Cookiebot?.show?.();
};
export const closeCookiebotDialog = () => {
    onHide();
    setTimeout(() => {
        window.Cookiebot?.hide?.();
    }, 200);
};

function getCookiebotMarketingConsent() {
    return Boolean(window.Cookiebot?.consent?.marketing);
}

export const checkConsentBefore3Months = () => {
    const marketing = getCookiebotMarketingConsent();
    const statistics = window.Cookiebot?.consent?.statistics;
    const preferences = window.Cookiebot?.consent?.preferences;
    const actualDate = moment();
    const consentDate = moment(window.Cookiebot?.consentUTC);
    const diff = moment(actualDate).diff(consentDate, 'days');
    if (diff > 90) {
        if (!marketing || !statistics || !preferences) {
            showCookiebotDialog();
        }
    }
};

export function useCookiebotMarketingConsent() {
    const [, forceUpdate] = useReducer(x => x + 1, 0);

    useEffect(() => {
        window.addEventListener('CookiebotOnAccept', forceUpdate);
        window.addEventListener('CookiebotOnDecline', forceUpdate);
        return () => {
            window.addEventListener('CookiebotOnAccept', forceUpdate);
            window.addEventListener('CookiebotOnDecline', forceUpdate);
        };
    }, []);

    return getCookiebotMarketingConsent();
}

window.addEventListener('CookiebotOnDialogDisplay', onDialogDisplay);
window.addEventListener('CookiebotOnAccept', onAccept);
window.addEventListener('CookiebotOnDecline', onDecline);
